import { useState, useEffect, useRef, useCallback } from "react";
import type { ChangeEvent } from "react";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { useLocation } from "@/contexts/LocationContext";
import {
  Search, ArrowLeft, Calendar, Upload, Plus, Loader2,
  Clock, PhoneCall, AlertTriangle, CheckCircle2, XCircle,
  Timer, PhoneMissed, RefreshCw, Mic, Phone, X, Video, Building2
} from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { toast } from "sonner";
import Papa from "papaparse";
import { v4 as uuidv4 } from "uuid";
import { AddAppointmentForm } from "./AddAppointmentForm";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import AuthService from "@/lib/auth.ts";
import CallService from "@/services/CallService";
import MainLayout from "@/layouts/MainLayout";
import { cn, formatPhoneDisplay } from "@/lib/utils";

type PatientEncounter = {
  patient_id: string;
  encounter_date: string;
  encounter_type: string;
  status: string;
  primary_provider_id: string;
  facility_id: number | null;
  department: number | null;
  chief_complaint: string | null;
  diagnosis_primary: string | null;
  diagnosis_secondary: string | null;
  start_time: string | null;
  end_time: string | null;
  visit_reason: string | null;
  visit_priority: string | null;
  clinical_notes: string | null;
  encounter_summary: string | null;
  follow_up_instructions: string | null;
  created_at: string;
  updated_at: string;
  sms_sent_at: string | null;
  sms_expires_at: string | null;
  encounter_review_status: string | null;
  review_recorded_by: string | null;
  encounter_id: string;
  location_ids: string[];
  secondary_provider_id: string | null;
  patients?: {
    id: string;
    first_name: string;
    middle_name?: string;
    last_name: string;
    mobile_number: string;
  };
};

interface PaginationState {
  page: number;
  pageSize: number;
  total: number;
}

// ─── Smart status derivation ──────────────────────────────────────────────────
// Given a DB status and the appointment datetime, derive the display status:
//   "missed"     → was scheduled, time has passed by >20 min, still "scheduled"
//   "upcoming"   → within 60 min window
//   "imminent"   → within 10 min (sub-bucket of upcoming, used for amber)
//   "completed"  → DB says completed
//   "cancelled"  → DB says cancelled
//   "scheduled"  → future, beyond 60 min window

type DerivedStatus = "imminent" | "upcoming" | "missed" | "completed" | "cancelled" | "scheduled" | "in-progress";

function deriveStatus(encounter: PatientEncounter): DerivedStatus {
  const dbStatus = (encounter.status || "").toLowerCase();
  if (dbStatus === "completed") return "completed";
  if (dbStatus === "cancelled") return "cancelled";
  if (dbStatus === "in-progress") return "in-progress";

  const apptTime = new Date(encounter.start_time || encounter.encounter_date).getTime();
  const now = Date.now();
  const diffMs = apptTime - now; // negative = past

  // Past by more than 20 minutes and still "scheduled" → Missed
  if (diffMs < -(20 * 60 * 1000)) return "missed";

  // Within 10 min before or 20 min after → imminent (call window open)
  if (diffMs <= 10 * 60 * 1000 && diffMs >= -(20 * 60 * 1000)) return "imminent";

  // Within 60 min before → upcoming (amber warning)
  if (diffMs <= 60 * 60 * 1000 && diffMs > 10 * 60 * 1000) return "upcoming";

  // Future beyond 60 min
  return "scheduled";
}

// Whether call can be initiated (matches PatientDetails logic: 10 min before → 20 min after)
function canInitiateCall(encounter: PatientEncounter): boolean {
  if ((encounter.status || "").toLowerCase() !== "scheduled") return false;
  if (encounter.encounter_type !== "TeleHealth") return false;
  const apptTime = new Date(encounter.start_time || encounter.encounter_date).getTime();
  const now = Date.now();
  const diffMs = apptTime - now;
  return diffMs <= 10 * 60 * 1000 && diffMs >= -(20 * 60 * 1000);
}

// Time-until display
function timeUntil(encounter: PatientEncounter): string {
  const apptTime = new Date(encounter.start_time || encounter.encounter_date).getTime();
  const diffMs = apptTime - Date.now();
  const absDiff = Math.abs(diffMs);
  const mins = Math.floor(absDiff / 60000);
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;

  if (diffMs < 0) {
    if (mins < 60) return `${mins}m ago`;
    return `${hrs}h ${remainMins}m ago`;
  }
  if (mins < 60) return `in ${mins}m`;
  return `in ${hrs}h ${remainMins > 0 ? ` ${remainMins}m` : ""}`;
}

// Status display config
const STATUS_CONFIG: Record<DerivedStatus, {
  label: string;
  icon: React.ElementType;
  rowClass: string;
  badgeClass: string;
  iconClass: string;
}> = {
  imminent: {
    label: "Call Now",
    icon: PhoneCall,
    rowClass: "bg-amber-50/60 border-l-4 border-l-amber-400",
    badgeClass: "bg-amber-100 text-amber-800 border-amber-200",
    iconClass: "text-amber-500",
  },
  upcoming: {
    label: "Upcoming",
    icon: Timer,
    rowClass: "bg-amber-50/30 border-l-4 border-l-amber-200",
    badgeClass: "bg-amber-50 text-amber-700 border-amber-200",
    iconClass: "text-amber-400",
  },
  missed: {
    label: "Missed",
    icon: PhoneMissed,
    rowClass: "bg-red-50/40 border-l-4 border-l-red-400",
    badgeClass: "bg-red-100 text-red-700 border-red-200",
    iconClass: "text-red-500",
  },
  completed: {
    label: "Completed",
    icon: CheckCircle2,
    rowClass: "border-l-4 border-l-emerald-300",
    badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
    iconClass: "text-emerald-500",
  },
  cancelled: {
    label: "Cancelled",
    icon: XCircle,
    rowClass: "border-l-4 border-l-gray-200 opacity-60",
    badgeClass: "bg-gray-100 text-gray-500 border-gray-200",
    iconClass: "text-gray-400",
  },
  scheduled: {
    label: "Scheduled",
    icon: Calendar,
    rowClass: "border-l-4 border-l-emerald-400",
    badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
    iconClass: "text-emerald-500",
  },
  "in-progress": {
    label: "In Progress",
    icon: Clock,
    rowClass: "bg-blue-50/40 border-l-4 border-l-blue-400",
    badgeClass: "bg-blue-100 text-blue-700 border-blue-200",
    iconClass: "text-blue-500",
  },
};

// Sort encounters: imminent first, then upcoming, then future by time, then missed, then completed/cancelled
function sortEncounters(encounters: PatientEncounter[]): PatientEncounter[] {
  const order: Record<DerivedStatus, number> = {
    imminent: 0,
    upcoming: 1,
    scheduled: 2,
    "in-progress": 3,
    missed: 4,
    completed: 5,
    cancelled: 6,
  };

  return [...encounters].sort((a, b) => {
    const statusA = deriveStatus(a);
    const statusB = deriveStatus(b);
    const orderDiff = order[statusA] - order[statusB];
    if (orderDiff !== 0) return orderDiff;

    // Within same bucket, sort by time ascending (soonest first)
    const timeA = new Date(a.start_time || a.encounter_date).getTime();
    const timeB = new Date(b.start_time || b.encounter_date).getTime();
    return timeA - timeB;
  });
}

const Appointments = () => {
  const { selectedLocationId, locations } = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [importing, setImporting] = useState(false);
  const [encounters, setEncounters] = useState<PatientEncounter[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [encounterTypeFilter, setEncounterTypeFilter] = useState<string>("");
  const navigate  = useNavigate();
  const routerLocation = useRouterLocation();
  const [modalOpen, setModalOpen] = useState(false);
  const [defaultPatientId, setDefaultPatientId] = useState<string | undefined>(undefined);
  const [callingEncounterId, setCallingEncounterId] = useState<string | null>(null);
  const [activeCall, setActiveCall] = useState<{
    encounterId: string;
    patientName: string;
    patientPhone?: string;
    startTime: number;
  } | null>(null);
  const [callDuration, setCallDuration] = useState(0);

  // Live timer for active call
  useEffect(() => {
    if (!activeCall) {
      setCallDuration(0);
      return;
    }
    const timer = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [activeCall]);

  const formatCallDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_now, setNow] = useState(Date.now());
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 10,
    total: 0,
  });

  // Tick every 30 seconds so status/time-until updates live
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(interval);
  }, []);

  const selectedLocationName =
    locations?.find((l) => l.value === selectedLocationId)?.label || "";

  const formatReadableDate = (dateString: string) => {
    if (!dateString) return "—";
    let date: Date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split("-").map(Number);
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(dateString);
    }
    const day = date.getDate();
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const getOrdinal = (d: number) => {
      if (d > 3 && d < 21) return "th";
      switch (d % 10) { case 1: return "st"; case 2: return "nd"; case 3: return "rd"; default: return "th"; }
    };
    const timeString = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    return `${day}${getOrdinal(day)} ${month} ${year}, ${timeString}`;
  };

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const results = await new Promise<any[]>((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header: string) => header.trim(),
          complete: (results: any) => {
            if (results.errors.length > 0) { reject(new Error("CSV parsing failed")); return; }
            resolve(results.data);
          },
          error: (error: any) => reject(new Error(error.message)),
        });
      });
      if (!results || results.length === 0) throw new Error("No data found");
      const encountersToImport = results.map(row => ({
        encounter_id: row.encounter_id || uuidv4(),
        patient_id: row.patient_id,
        encounter_date: row.encounter_date,
        encounter_type: row.encounter_type,
        status: row.status,
        primary_provider_id: row.primary_provider_id || null,
        location_ids: Array.isArray(row.location_ids) ? row.location_ids : [row.location_ids],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));
      const res = await AuthService.createAppointments(encountersToImport);
      if (res.error) throw new Error(res.error);
      toast.success(`Successfully imported ${encountersToImport.length} appointments`);
      await fetchEncounters();
    } catch (error: any) {
      toast.error(error.message || "Failed to import appointments");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const fetchEncounters = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!selectedLocationId) {
        setEncounters([]);
        setPagination(prev => ({ ...prev, total: 0 }));
        return;
      }
      // Derived statuses (missed/upcoming/imminent) are frontend-computed from
      // DB status "scheduled" + time — don't send them to the backend.
      const backendStatus = statusFilter.startsWith("derived_") ? "scheduled" : statusFilter;
      const response = await AuthService.getAppointments({
        locationId: selectedLocationId,
        status: backendStatus,
        type: encounterTypeFilter,
        search: searchTerm,
        page: pagination.page,
        pageSize: pagination.pageSize
      });
      const rawEncounters: PatientEncounter[] = response.data || [];
      if (rawEncounters.length > 0) {
        const uniquePatientIds = Array.from(new Set(rawEncounters.map((e) => e.patient_id).filter(Boolean)));
        try {
          const patientsResult = await AuthService.getPatientsWithFilters({ patientIds: uniquePatientIds });
          const patientMap = new Map((patientsResult.data || []).map((p: any) => [p.id, p]));
          const enriched = rawEncounters.map((enc) => ({
            ...enc,
            patients: enc.patients ?? (patientMap.get(enc.patient_id) ? {
              id: patientMap.get(enc.patient_id).id,
              first_name: patientMap.get(enc.patient_id).first_name || "",
              middle_name: patientMap.get(enc.patient_id).middle_name || "",
              last_name: patientMap.get(enc.patient_id).last_name || "",
              mobile_number: patientMap.get(enc.patient_id).mobile_number || "",
            } : undefined),
          }));
          setEncounters(enriched);
        } catch {
          setEncounters(rawEncounters);
        }
      } else {
        setEncounters([]);
      }
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast.error("Failed to load appointments");
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocationId, searchTerm, statusFilter, encounterTypeFilter, pagination.page, pagination.pageSize]);

  useEffect(() => { fetchEncounters(); }, [selectedLocationId]);

  // Auto-open the Add Appointment modal when navigated here from another page
  // (e.g. "Schedule a Call" button on a questionnaire response).
  // The caller passes: navigate("/appointments", { state: { openAddModal: true, patientId: "..." } })
  useEffect(() => {
    const state = routerLocation.state as { openAddModal?: boolean; patientId?: string } | null;
    if (state?.openAddModal) {
      setDefaultPatientId(state.patientId || undefined);
      setModalOpen(true);
      // Clear state so a page refresh doesn't re-open the modal
      navigate("/appointments", { replace: true, state: {} });
    }
  }, [routerLocation.state]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => { fetchEncounters(); }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, statusFilter, encounterTypeFilter, pagination.page, pagination.pageSize]);

  const handleCallNow = async (encounter: PatientEncounter) => {
    setCallingEncounterId(encounter.encounter_id);
    const p = encounter.patients;
    const patientName = p ? `${p.first_name || ""} ${p.last_name || ""}`.trim() : "Patient";
    const patientPhone = p?.mobile_number;

    // Display active in-call banner with timer & mic on
    setActiveCall({
      encounterId: encounter.encounter_id,
      patientName,
      patientPhone: patientPhone || undefined,
      startTime: Date.now(),
    });

    // Notify ConnectCCPWidget to open the Amazon Connect phone dialpad
    window.dispatchEvent(
      new CustomEvent("connect:open-phone", {
        detail: {
          patientName,
          patientPhone,
          patientId: encounter.patient_id,
          encounterId: encounter.encounter_id,
        },
      })
    );

    try {
      const result = await CallService.makeCallNow(encounter.patient_id);
      if (result.success) {
        await fetchEncounters();
      } else {
        setTimeout(() => setActiveCall(null), 5000);
      }
    } catch {
      setActiveCall(null);
    } finally {
      setCallingEncounterId(null);
    }
  };

  // Sorted encounters for display — re-computed every `now` tick
  // Also applies derived status filters (missed/upcoming/imminent) on the frontend
  const sortedEncounters = sortEncounters(
    encounters.filter((enc) => {
      const p = enc.patients;
      if (!p) return true;
      if (p.first_name === "Registration" && p.last_name === "Pending") return false;
      // Apply derived status filter if selected
      if (statusFilter === "derived_imminent") return deriveStatus(enc) === "imminent";
      if (statusFilter === "derived_upcoming") return deriveStatus(enc) === "upcoming";
      if (statusFilter === "derived_missed")   return deriveStatus(enc) === "missed";
      return true;
    })
  );

  // Legend counts
  const imminentCount = sortedEncounters.filter(e => deriveStatus(e) === "imminent").length;
  const upcomingCount = sortedEncounters.filter(e => deriveStatus(e) === "upcoming").length;
  const missedCount   = sortedEncounters.filter(e => deriveStatus(e) === "missed").length;

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#f7f8fa] p-6 lg:p-10 font-sans">
        <div className="max-w-7xl mx-auto space-y-8">

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <Button
                variant="outline"
                size="icon"
                onClick={() => navigate(-1)}
                className="h-12 w-12 rounded-2xl border-gray-100 bg-white shadow-sm hover:shadow-md transition-all"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </Button>
              <div>
                <h1 className="text-3xl lg:text-4xl font-extrabold text-[#1a7a4a] tracking-tight">
                  Appointments
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-gray-500 font-medium">Manage and schedule patient visits</p>
                  {selectedLocationId && (
                    <Badge variant="outline" className="text-[10px] h-5 bg-white border-gray-100 text-[#1a7a4a] uppercase tracking-wider font-bold px-2">
                      {selectedLocationName}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".csv" className="hidden" />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing || !selectedLocationId}
                className="h-11 px-6 rounded-2xl border-gray-200 text-gray-600 hover:text-[#1a7a4a] hover:bg-gray-50 transition-all font-semibold flex items-center gap-2"
              >
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Import CSV
              </Button>
              <Button
                variant="outline"
                onClick={fetchEncounters}
                disabled={isLoading || !selectedLocationId}
                className="h-11 px-4 rounded-2xl border-gray-200 text-gray-600 hover:text-[#1a7a4a] hover:bg-gray-50 transition-all font-semibold flex items-center gap-2"
              >
                <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
              </Button>
              <Button
                onClick={() => setModalOpen(true)}
                disabled={!selectedLocationId}
                className="h-11 px-6 rounded-2xl bg-[#1a7a4a] hover:bg-[#15633c] text-white shadow-lg shadow-green-100 transition-all hover:scale-[1.02] flex items-center gap-2 font-semibold"
              >
                <Plus className="h-4 w-4" />
                Schedule Appointment
              </Button>
            </div>
          </div>

          {/* Active In-Call Banner */}
          {activeCall && (
            <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-green-50 border-2 border-emerald-500/40 rounded-2xl p-4 shadow-md flex items-center justify-between flex-wrap gap-3 animate-in fade-in slide-in-from-top-3 duration-200">
              <div className="flex items-center gap-3.5">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white shadow-md shadow-emerald-200">
                    <PhoneCall className="w-5 h-5 animate-pulse" />
                  </div>
                  <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full animate-ping" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-800 text-sm">
                      Call in Progress with {activeCall.patientName}
                    </h3>
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] font-semibold py-0.5 px-2">
                      Live Call
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-600 mt-0.5">
                    <span className="flex items-center gap-1 font-mono text-emerald-700 font-semibold">
                      <Clock className="w-3.5 h-3.5 text-emerald-600" />
                      {formatCallDuration(callDuration)}
                    </span>
                    <span className="flex items-center gap-1 font-medium text-emerald-700">
                      <Mic className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
                      Microphone Active
                    </span>
                    {activeCall.patientPhone && (
                      <span className="text-slate-500">
                        {activeCall.patientPhone}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent("connect:open-phone"))}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-semibold h-9 px-3.5 flex items-center gap-1.5 shadow-sm"
                >
                  <Phone className="w-3.5 h-3.5" />
                  Open Softphone Window
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  type="button"
                  onClick={() => setActiveCall(null)}
                  className="text-slate-400 hover:text-slate-700 rounded-xl text-xs h-9 px-2.5"
                  title="Dismiss call banner"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Status legend pills — only show when data loaded */}
          {encounters.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {imminentCount > 0 && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-2 text-amber-800 text-xs font-bold">
                  <PhoneCall className="h-3.5 w-3.5 text-amber-500" />
                  {imminentCount} call window{imminentCount > 1 ? "s" : ""} open
                </div>
              )}
              {upcomingCount > 0 && (
                <div className="flex items-center gap-2 bg-amber-50/60 border border-amber-100 rounded-2xl px-4 py-2 text-amber-700 text-xs font-bold">
                  <Clock className="h-3.5 w-3.5 text-amber-400" />
                  {upcomingCount} within 60 min
                </div>
              )}
              {missedCount > 0 && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-2xl px-4 py-2 text-red-700 text-xs font-bold">
                  <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                  {missedCount} missed
                </div>
              )}
              <div className="flex items-center gap-3 ml-auto text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />Call window</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-200 inline-block" />Within 60 min</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block" />Missed</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block" />On track</span>
              </div>
            </div>
          )}

          {/* Search and Filters */}
          <Card className="rounded-3xl border-gray-100 shadow-xl shadow-gray-200/50 bg-white overflow-visible">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="relative flex-grow group">
                  <input
                    type="text"
                    placeholder="Search by patient name..."
                    className="pl-12 pr-4 h-12 w-full bg-gray-50/50 border-transparent focus:bg-white focus:border-[#1a7a4a] focus:ring-[#1a7a4a] rounded-2xl text-sm transition-all shadow-sm group-hover:shadow-md"
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                    onKeyPress={(e: React.KeyboardEvent) => e.key === "Enter" && fetchEncounters()}
                    disabled={!selectedLocationId}
                  />
                  <Search className="absolute left-4 top-3.5 h-5 w-5 text-gray-400 group-hover:text-[#1a7a4a] transition-colors" />
                </div>

                <div className="flex flex-wrap gap-4">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full lg:w-[180px] h-12 rounded-2xl border-gray-100 bg-white hover:bg-gray-50 transition-all font-semibold">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-gray-100 shadow-xl">
                      <SelectItem value="all">All Statuses</SelectItem>
                      {/* DB statuses — sent to backend */}
                      <SelectItem value="scheduled">Scheduled (All)</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="in-progress">In Progress</SelectItem>
                      {/* Derived statuses — filtered on frontend after fetch */}
                      <SelectItem value="derived_imminent">🟠 Call Window Open</SelectItem>
                      <SelectItem value="derived_upcoming">🟡 Upcoming (60 min)</SelectItem>
                      <SelectItem value="derived_missed">🔴 Missed</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={encounterTypeFilter} onValueChange={setEncounterTypeFilter}>
                    <SelectTrigger className="w-full lg:w-[180px] h-12 rounded-2xl border-gray-100 bg-white hover:bg-gray-50 transition-all font-semibold">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-gray-100 shadow-xl">
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="InPerson">In-Person</SelectItem>
                      <SelectItem value="TeleHealth">Telehealth</SelectItem>
                      <SelectItem value="SMS_Survey">SMS Survey</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button
                    onClick={fetchEncounters}
                    disabled={isLoading || !selectedLocationId}
                    className="h-12 px-8 rounded-2xl bg-[#1a7a4a] hover:bg-[#15633c] text-white font-bold transition-all shadow-lg shadow-green-100 hover:scale-[1.02]"
                  >
                    {isLoading ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Searching...
                      </div>
                    ) : "Search"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          <div className="space-y-4">
            {!selectedLocationId ? (
              <div className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-gray-200/50 p-20 text-center">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Calendar className="h-10 w-10 text-gray-200" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No location selected</h3>
                <p className="text-gray-500 text-lg">Please select a Hospital and Branch from the header to view appointments.</p>
              </div>
            ) : isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a7a4a]"></div>
              </div>
            ) : sortedEncounters.length > 0 ? (
              <Card className="rounded-3xl border-gray-100 shadow-xl shadow-gray-200/50 bg-white overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50/50 border-b border-gray-100">
                        <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500">Patient</th>
                        <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500">Date & Time</th>
                        <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500 text-center">Type</th>
                        <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500 text-center">Status</th>
                        <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500">Chief Complaint</th>
                        <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {sortedEncounters.map((encounter) => {
                        const derived = deriveStatus(encounter);
                        const config = STATUS_CONFIG[derived];
                        const StatusIcon = config.icon;
                        const isTelehealth = encounter.encounter_type === "TeleHealth";
                        const callOpen = canInitiateCall(encounter);
                        const isCalling = callingEncounterId === encounter.encounter_id;

                        const patientName = [
                          encounter.patients?.first_name,
                          encounter.patients?.middle_name,
                          encounter.patients?.last_name,
                        ].filter(Boolean).join(" ");

                        return (
                          <tr
                            key={encounter.encounter_id}
                            className={cn(
                              "group transition-colors",
                              config.rowClass,
                              "hover:brightness-95"
                            )}
                          >
                            {/* Patient */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#1a7a4a]/10 flex items-center justify-center text-[#1a7a4a] font-bold text-sm flex-shrink-0">
                                  {encounter.patients?.first_name?.[0]}{encounter.patients?.last_name?.[0]}
                                </div>
                                <div
                                  className="font-bold text-gray-900 cursor-pointer hover:text-[#1a7a4a] transition-colors leading-tight"
                                  onClick={() => navigate(`/patients/${encounter.patient_id}`)}
                                >
                                  {patientName || <span className="text-gray-400 font-normal">Unknown Patient</span>}
                                  <div className="text-xs font-mono text-gray-400 mt-0.5">
                                    {formatPhoneDisplay(encounter.patients?.mobile_number) || "No number"}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Date & Time */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-semibold text-gray-700">
                                {formatReadableDate(encounter.start_time || encounter.encounter_date)}
                              </div>
                              {/* Time-until chip for non-completed/cancelled */}
                              {(derived === "imminent" || derived === "upcoming" || derived === "scheduled" || derived === "missed") && (
                                <div className={cn(
                                  "inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold",
                                  derived === "imminent" ? "bg-amber-100 text-amber-700" :
                                  derived === "upcoming" ? "bg-amber-50 text-amber-600" :
                                  derived === "missed" ? "bg-red-100 text-red-600" :
                                  "bg-emerald-50 text-emerald-600"
                                )}>
                                  <Clock className="h-2.5 w-2.5" />
                                  {timeUntil(encounter)}
                                </div>
                              )}
                            </td>

                            {/* Type */}
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              {encounter.encounter_type === "InPerson" ? (
                                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold border border-emerald-200 bg-emerald-50 text-emerald-700 tracking-wide">
                                  <Building2 className="h-3 w-3" />
                                  In-Person
                                </div>
                              ) : encounter.encounter_type === "TeleHealth" ? (
                                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold border border-indigo-200 bg-indigo-50 text-indigo-700 tracking-wide">
                                  <Video className="h-3 w-3" />
                                  Telehealth
                                </div>
                              ) : encounter.encounter_type === "RemoteTheropyMonitoring" ? (
                                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold border border-purple-200 bg-purple-50 text-purple-700 tracking-wide">
                                  Behavioral Health
                                </div>
                              ) : (
                                <div className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold border border-gray-100 bg-gray-50 text-gray-600 uppercase tracking-wider">
                                  {encounter.encounter_type === "SMS_Survey" ? "SMS Survey" : encounter.encounter_type}
                                </div>
                              )}
                            </td>

                            {/* Status */}
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className={cn(
                                "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold border uppercase tracking-wider",
                                config.badgeClass
                              )}>
                                <StatusIcon className={cn("h-3 w-3", config.iconClass)} />
                                {config.label}
                              </div>
                            </td>

                            {/* Chief Complaint */}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium italic">
                              {encounter.chief_complaint || "—"}
                            </td>

                            {/* Action — Call Now button for Telehealth in call window */}
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              {isTelehealth && callOpen ? (
                                <Button
                                  size="sm"
                                  disabled={isCalling}
                                  onClick={() => handleCallNow(encounter)}
                                  className="h-8 px-4 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold shadow-md shadow-amber-200 transition-all hover:scale-[1.03] flex items-center gap-1.5 mx-auto"
                                >
                                  {isCalling ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <PhoneCall className="h-3 w-3" />
                                  )}
                                  {isCalling ? "Calling…" : "Call Now"}
                                </Button>
                              ) : isTelehealth && derived === "upcoming" ? (
                                <span className="text-[10px] text-amber-500 font-semibold">
                                  Opens soon
                                </span>
                              ) : isTelehealth && derived === "scheduled" ? (
                                <span className="text-[10px] text-gray-400 font-medium">
                                  Call window opens 10 min before
                                </span>
                              ) : derived === "missed" ? (
                                <span className="text-[10px] text-red-400 font-semibold">
                                  Window expired
                                </span>
                              ) : (
                                <span className="text-[10px] text-gray-300">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : (
              <div className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-gray-200/50 p-20 text-center">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Calendar className="h-10 w-10 text-gray-200" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No appointments found</h3>
                <p className="text-gray-500 text-lg">Try adjusting your filters or search terms.</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {selectedLocationId && pagination.total > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2 mt-6">
              <div className="text-sm text-gray-500 font-medium bg-white px-4 py-2 rounded-2xl border border-gray-100 shadow-sm">
                Showing{" "}
                <span className="text-[#1a7a4a] font-bold">
                  {(pagination.page - 1) * pagination.pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="text-[#1a7a4a] font-bold">
                  {Math.min(pagination.page * pagination.pageSize, pagination.total)}
                </span>{" "}
                of <span className="text-gray-900 font-bold">{pagination.total}</span>{" "}
                appointments
              </div>

              <div className="flex items-center gap-3">
                <div className="flex bg-white rounded-2xl border border-gray-100 shadow-sm p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1 || isLoading}
                    className="h-9 px-4 rounded-xl text-xs font-bold hover:bg-gray-50"
                  >
                    Previous
                  </Button>
                  <div className="w-px h-4 bg-gray-100 self-center mx-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page * pagination.pageSize >= pagination.total || isLoading}
                    className="h-9 px-4 rounded-xl text-xs font-bold hover:bg-gray-50"
                  >
                    Next
                  </Button>
                </div>

                <select
                  className="h-11 rounded-2xl border border-gray-100 bg-white px-4 py-1 text-sm font-bold text-gray-600 shadow-sm focus:ring-2 focus:ring-[#1a7a4a]/20 outline-none transition-all"
                  value={pagination.pageSize}
                  onChange={(e) => setPagination((prev) => ({ ...prev, pageSize: Number(e.target.value), page: 1 }))}
                  disabled={isLoading}
                >
                  <option value="5">5 / page</option>
                  <option value="10">10 / page</option>
                  <option value="20">20 / page</option>
                  <option value="50">50 / page</option>
                </select>
              </div>
            </div>
          )}

          {/* Add Appointment Modal */}
          <Dialog.Root open={modalOpen} onOpenChange={(open) => { setModalOpen(open); if (!open) setDefaultPatientId(undefined); }}>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 animate-in fade-in duration-300" />
              <Dialog.Content className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] w-full max-w-xl bg-white rounded-3xl shadow-2xl z-50 animate-in zoom-in-95 duration-300 overflow-hidden border-none">
                <div className="p-8 pb-0">
                  <div className="flex items-center justify-between mb-2">
                    <Dialog.Title className="text-2xl font-extrabold text-[#1a7a4a] tracking-tight">
                      Schedule Appointment
                    </Dialog.Title>
                    <Dialog.Close className="h-10 w-10 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all">
                      <Plus className="h-5 w-5 rotate-45" />
                    </Dialog.Close>
                  </div>
                  <Dialog.Description className="text-gray-500 font-medium mb-6">
                    Fill in the details to schedule a new patient visit.
                  </Dialog.Description>
                </div>
                <div className="px-8 pb-8 max-h-[70vh] overflow-y-auto custom-scrollbar">
                  <AddAppointmentForm
                    defaultPatientId={defaultPatientId}
                    onSuccess={() => {
                      setModalOpen(false);
                      fetchEncounters();
                    }}
                  />
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>

        </div>
      </div>
    </MainLayout>
  );
};

export default Appointments;
